import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { showWarningToast } from '@/lib/toast';
import { toUserMessage } from '@/lib/transient';
import { cn } from '@/lib/utils';
import { formatDateTime, fromLocalInput, loadNumber, toLocalInput } from '../format';
import { fleetKeys, useLoads, useTrucks } from '../hooks';
import { previewSimulation, type SimSource } from './api';
import { dayRange } from './format';
import { useSimulationActions } from './hooks';

const DAY_MS = 86_400_000;

type Mode = 'tura' | 'truck';

/** A tura's number as typed: "L-1234", "l1234" or "1234" */
const numberOf = (text: string): number | null => {
  const digits = /^l?-?\s*(\d+)$/i.exec(text.trim())?.[1];
  return digits ? Number(digits) : null;
};

/** Super admin: pick a tura, or a truck and a day; its range and the turas it copies are previewed, then Start */
export function SimulationStart({
  simTrucks,
  onClose,
}: {
  simTrucks: Array<{ id: string; plate: string }>;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const actions = useSimulationActions();
  const [mode, setMode] = useState<Mode>('tura');
  const [turaText, setTuraText] = useState('');
  const [truckId, setTruckId] = useState('');
  const [day, setDay] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);

  // Turas from two months back to a week ahead: where a dispatcher would pick a tura to replay
  const [window] = useState(() => ({
    from: new Date(Date.now() - 60 * DAY_MS).toISOString(),
    to: new Date(Date.now() + 7 * DAY_MS).toISOString(),
  }));
  const loads = useLoads(window);
  const trucks = useTrucks();
  const realTrucks = (trucks.data?.data ?? []).filter(
    (truck) => (truck.traccarDeviceId ?? 0) >= 0 && !truck.plate.startsWith('SIM ')
  );

  const number = numberOf(turaText);
  const load = number === null ? null : (loads.data?.find((candidate) => candidate.number === number) ?? null);

  const source: SimSource | null = useMemo(() => {
    if (mode === 'tura') return load ? { loadId: load.id } : null;
    return truckId && day ? { truckId, ...dayRange(day) } : null;
  }, [mode, load, truckId, day]);
  const sourceKey = source ? JSON.stringify(source) : '';

  // retry off: a failed preview is shown, not asked again
  const previewQuery = useQuery({
    queryKey: [...fleetKeys.all, 'simulation', 'preview', sourceKey],
    queryFn: () => previewSimulation(source!),
    enabled: !!source,
    retry: false,
    staleTime: 0,
  });
  const preview = source ? (previewQuery.data ?? null) : null;
  const previewing = !!source && previewQuery.isFetching;
  const failed =
    source && previewQuery.error ? toUserMessage(previewQuery.error, t('fleet.simulation.previewFailed')) : null;

  // The range starts as the preview's; an edit holds for that source only
  const [edit, setEdit] = useState<{ key: string; from?: string; to?: string }>({ key: '' });
  const own = edit.key === sourceKey ? edit : null;
  const from = own?.from ?? (preview ? toLocalInput(preview.from) : '');
  const to = own?.to ?? (preview ? toLocalInput(preview.to) : '');
  const setFrom = (value: string) => setEdit({ ...own, key: sourceKey, from: value });
  const setTo = (value: string) => setEdit({ ...own, key: sourceKey, to: value });

  const fromIso = fromLocalInput(from);
  const toIso = fromLocalInput(to);
  const rangeOk =
    !!fromIso && !!toIso && fromIso < toIso && new Date(toIso).getTime() - new Date(fromIso).getTime() <= 7 * DAY_MS;

  const start = () => {
    if (!source || !fromIso || !toIso) return;
    // Not mutate's own onSuccess: the session's panel replaces this dialog first, and an unmounted caller's callbacks
    // never run, so the launcher would reopen the dialog once the session ends
    actions.start
      .mutateAsync({ source, from: fromIso, to: toIso })
      .then(onClose, (error: unknown) => showWarningToast(toUserMessage(error, t('fleet.simulation.startFailed'))));
  };

  const remove = (id: string) =>
    actions.remove.mutate(id, {
      onSettled: () => setConfirming(null),
      onError: (error) => showWarningToast(toUserMessage(error, t('fleet.simulation.deleteFailed'))),
    });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-qa-id="simulation-start-dialog">
        <DialogHeader>
          <DialogTitle>{t('fleet.simulation.title')}</DialogTitle>
          <DialogDescription>{t('fleet.simulation.intro')}</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1.5">
          {(['tura', 'truck'] as const).map((value) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={mode === value ? 'default' : 'outline'}
              onClick={() => setMode(value)}
            >
              {t(`fleet.simulation.mode.${value}`)}
            </Button>
          ))}
        </div>

        {mode === 'tura' ? (
          <div className="space-y-1.5">
            <Label htmlFor="sim-tura">{t('fleet.simulation.tura')}</Label>
            <Input
              id="sim-tura"
              value={turaText}
              onChange={(event) => setTuraText(event.target.value)}
              placeholder="L-1234"
              data-qa-id="simulation-tura"
            />
            {number !== null && !loads.isPending && (
              <p className="text-sm text-muted-foreground">
                {load
                  ? `${loadNumber(load.number)} · ${load.origin} → ${load.destination}`
                  : t('fleet.simulation.noTura')}
              </p>
            )}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="sim-truck">{t('fleet.simulation.truck')}</Label>
              <Select value={truckId} onValueChange={setTruckId}>
                <SelectTrigger id="sim-truck" className="w-full" data-qa-id="simulation-truck">
                  <SelectValue placeholder={t('fleet.simulation.pickTruck')} />
                </SelectTrigger>
                <SelectContent>
                  {realTrucks.map((truck) => (
                    <SelectItem key={truck.id} value={truck.id}>
                      {truck.plate}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sim-day">{t('fleet.simulation.day')}</Label>
              <Input
                id="sim-day"
                type="date"
                value={day}
                onChange={(event) => setDay(event.target.value)}
                data-qa-id="simulation-day"
              />
            </div>
          </div>
        )}

        {previewing && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
        {failed && <p className="text-sm text-destructive">{failed}</p>}
        {preview && (
          <div className="space-y-3" data-qa-id="simulation-preview">
            <p className="text-sm">{t('fleet.simulation.copies', { plate: preview.truck.plate })}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="sim-from">{t('fleet.simulation.from')}</Label>
                <Input
                  id="sim-from"
                  type="datetime-local"
                  value={from}
                  onChange={(event) => setFrom(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sim-to">{t('fleet.simulation.to')}</Label>
                <Input id="sim-to" type="datetime-local" value={to} onChange={(event) => setTo(event.target.value)} />
              </div>
            </div>
            {!rangeOk && <p className="text-sm text-destructive">{t('fleet.simulation.badRange')}</p>}
            <div>
              <p className="mb-1 text-sm font-medium">{t('fleet.simulation.turas')}</p>
              {preview.turas.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('fleet.simulation.noTuras')}</p>
              ) : (
                <ul className="space-y-0.5 text-sm text-muted-foreground">
                  {preview.turas.map((tura) => (
                    <li key={tura.id}>
                      {loadNumber(tura.number)} · {tura.route}
                      {tura.pickupAt && ` · ${formatDateTime(tura.pickupAt, i18n.language)}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('fleet.simulation.cancel')}
          </Button>
          <Button
            onClick={start}
            disabled={!preview || !rangeOk || actions.start.isPending}
            data-qa-id="simulation-start"
          >
            {actions.start.isPending && <Loader2 className="size-4 animate-spin" />}
            {t('fleet.simulation.start')}
          </Button>
        </DialogFooter>

        {simTrucks.length > 0 && (
          <div className="border-t pt-3">
            <p className="mb-1.5 text-sm font-medium">{t('fleet.simulation.simTrucks')}</p>
            <ul className="space-y-1">
              {simTrucks.map((truck) => (
                <li key={truck.id} className="flex items-center justify-between gap-2 text-sm">
                  <span>{truck.plate}</span>
                  {confirming === truck.id ? (
                    <span className="flex items-center gap-1.5">
                      <span className="text-muted-foreground">{t('fleet.simulation.deleteAsk')}</span>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={actions.remove.isPending}
                        onClick={() => remove(truck.id)}
                      >
                        {t('fleet.simulation.delete')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirming(null)}>
                        {t('fleet.simulation.cancel')}
                      </Button>
                    </span>
                  ) : (
                    <Button
                      size="icon"
                      variant="ghost"
                      className={cn('size-7')}
                      aria-label={t('fleet.simulation.delete')}
                      onClick={() => setConfirming(truck.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
